import { memo, useEffect } from 'react';
import Lottie from 'react-lottie';

import { ProgressBar, Spinner, Text } from '@/lib/v2/components';

import okData from '@/src/modules/ContactsModule/screens/ImportMain/components/lotties/animation_one.json';
import mappingData from '@/src/modules/ContactsModule/screens/ImportMain/components/lotties/animation_three.json';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';

interface UploadProgressProps {
  text: string;
  textMappingFieldsCompleted?: string;
  percentage: number;
  isLoadingUploadFile?: boolean;
  isLoadingMapFields?: boolean;
  isMappingFieldsCompleted?: boolean;
  onUploadFileCompleted?: () => void;
  onCompletionAnimationEnd?: () => void;
  animationDuration?: number;
}

const UploadProgress = ({
  text,
  textMappingFieldsCompleted,
  percentage,
  isLoadingUploadFile,
  isLoadingMapFields,
  isMappingFieldsCompleted,
  onUploadFileCompleted,
  onCompletionAnimationEnd,
  animationDuration = 3000,
}: UploadProgressProps) => {
  const uploadCompleted = percentage >= 100;

  useEffect(() => {
    if (uploadCompleted && isLoadingUploadFile) {
      onUploadFileCompleted?.();
    }
  }, [isLoadingUploadFile, onUploadFileCompleted, uploadCompleted]);

  useEffect(() => {
    let time: string | number | NodeJS.Timeout | undefined;
    if (isMappingFieldsCompleted) {
      time = setTimeout(() => {
        onCompletionAnimationEnd?.();
      }, animationDuration);
    }

    return () => {
      clearTimeout(time);
    };
  }, [animationDuration, isMappingFieldsCompleted, onCompletionAnimationEnd]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: !isMappingFieldsCompleted ? mappingData : okData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <div className="border-lightblue-500 relative flex h-3/4 flex-col items-center justify-center rounded-3xl border-4 border-dashed bg-emblue-secondary-hover p-10 text-center">
      {isLoadingUploadFile && !isLoadingMapFields && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col items-center gap-1">
            <Lottie options={defaultOptions} width={150} />
            <Text>{text}</Text>
            <Text color="primary" variant="headline">
              {percentage} %
            </Text>
          </div>
          <ProgressBar percentage={percentage} status={loadingStatus.loading} />
        </div>
      )}
      {isLoadingMapFields && !isLoadingUploadFile && (
        <>
          <Lottie options={defaultOptions} width={150} />
          <Text>{text}</Text>
          <Spinner withoutOverlay />
        </>
      )}
      {isMappingFieldsCompleted && (
        <>
          {textMappingFieldsCompleted && <Text>{textMappingFieldsCompleted}</Text>}
          <Lottie options={defaultOptions} width={200} />
        </>
      )}
    </div>
  );
};

export default memo(UploadProgress);
